import { CircleHelp, User } from "lucide-react";
import React, { useState } from "react";
import { isPregODonnell, isTaylorDay } from "@/shared/utils/firm";

import analyticsWhiteIcon from "@/assets/icons/analyticsWhite.svg";
import calendarSidebarWhiteIcon from "@/assets/icons/calendarWhite.svg";
import combineWhiteIcon from "@/assets/icons/trackWhite.svg";
import settingsWhiteIcon from "@/assets/icons/settingsWhite.svg";
import styles from "./Sidebar.module.css";
import trackWhiteIcon from "@/assets/icons/combineWhite.svg";

// Sidebar brand logo: prefer user-provided wide PNG in public; fallback to SVGs
const brandLogoPath = "/Logo_WhiteL.png";

type View = "entries" | "history" | "calendar" | "settings" | "support" | "faq";

export interface SidebarProps {
	sidebarOpen: boolean;
	viewMode: View;
	onChangeView: (v: View) => void;
	isClioFirm: boolean;
	clioConnected: boolean;
	onClioConnect: () => void;
	onClearSelection: () => void;
	onCalendarSelected?: () => void;
	minimal?: boolean;
	onSwitchToAdmin?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
	sidebarOpen,
	viewMode,
	onChangeView,
	isClioFirm,
	clioConnected,
	onClioConnect,
	onClearSelection,
	onCalendarSelected,
	minimal = false,
	onSwitchToAdmin,
}) => {
	// Sidebar always has dark brand background, so always use light/white icons
	const useLightIcons = true;

	// "New" badges for Support/FAQ — show until first click (excluded firms always seen)
	const badgesDisabled = isPregODonnell() || isTaylorDay();
	const [seenPages, setSeenPages] = useState(() => ({
		support: badgesDisabled || !!localStorage.getItem("hourglass_seen_support"),
		faq: badgesDisabled || !!localStorage.getItem("hourglass_seen_faq"),
	}));

	const markSeen = (page: "support" | "faq") => {
		localStorage.setItem(`hourglass_seen_${page}`, "1");
		setSeenPages((prev) => ({ ...prev, [page]: true }));
	};

	return (
		<div
			className={`${styles.sidebar} ${!sidebarOpen ? styles.collapsed : ""}`}
		>
			<div className={styles.navContainer}>
				<div className={styles.navTop}>
					<div className={styles.navItem}>
						<div
							className={`${styles.viewButtonLogo} ${!sidebarOpen ? styles.iconOnly : ""}`}
							style={{ width: "auto", maxWidth: "none" }}
						>
							<img
								src={brandLogoPath}
								alt="Hourglass"
								className={styles.sidebarBrandLogo}
							/>
						</div>
					</div>

					{!minimal && (
						<div className={styles.navItem}>
							<div
								className={`${styles.viewButton} ${styles.brandSidebarButton} ${viewMode === "entries" ? styles.viewButtonSelected : ""} ${!sidebarOpen ? styles.iconOnly : ""}`}
								onClick={() => {
									onChangeView("entries");
									onClearSelection();
								}}
							>
								<img
									src={useLightIcons ? trackWhiteIcon : trackWhiteIcon}
									alt="Tracked icon"
									className={styles.viewButtonIcon}
								/>
								{sidebarOpen && "Tracked"}
							</div>
						</div>
					)}

					{!minimal && (
						<div className={styles.navItem}>
							<div
								className={`${styles.viewButton} ${styles.brandSidebarButton} ${viewMode === "history" ? styles.viewButtonSelected : ""} ${!sidebarOpen ? styles.iconOnly : ""}`}
								onClick={() => {
									onChangeView("history");
									onClearSelection();
								}}
							>
								<img
									src={useLightIcons ? combineWhiteIcon : combineWhiteIcon}
									alt="Exported icon"
									className={styles.viewButtonIcon}
								/>
								{sidebarOpen && "Exported"}
							</div>
						</div>
					)}

					{!minimal && (
						<div className={`${styles.navItem} ${styles.mobileHideCalendar}`}>
							<div
								className={`${styles.viewButton} ${styles.brandSidebarButton} ${viewMode === "calendar" ? styles.viewButtonSelected : ""} ${!sidebarOpen ? styles.iconOnly : ""}`}
								onClick={() => {
									onChangeView("calendar");
									onClearSelection();
									onCalendarSelected?.();
								}}
							>
								<img
									src={
										useLightIcons
											? calendarSidebarWhiteIcon
											: calendarSidebarWhiteIcon
									}
									alt="Calendar icon"
									className={styles.viewButtonIcon}
								/>
								{sidebarOpen && "Calendar"}
							</div>
						</div>
					)}
				</div>

				<div className={styles.navBottom}>
					{!minimal && isClioFirm && (
						<div className={`${styles.navItem} ${styles.mobileHideSettings}`}>
							<div
								className={
									clioConnected
										? `${styles.clioConnectButton} ${styles.clioConnected}`
										: styles.clioConnectButton
								}
								onClick={!clioConnected ? onClioConnect : undefined}
							>
								{clioConnected ? "Connected with Clio" : "Connect to Clio"}
							</div>
						</div>
					)}

					{!minimal && (
						<div className={`${styles.navItem} ${styles.mobileHideSettings}`}>
							<div
								className={`${styles.viewButton} ${styles.brandSidebarButton} ${viewMode === "settings" ? styles.viewButtonSelected : ""} ${!sidebarOpen ? styles.iconOnly : ""}`}
								onClick={() => {
									onChangeView("settings");
									onClearSelection();
								}}
							>
								<img
									src={useLightIcons ? settingsWhiteIcon : settingsWhiteIcon}
									alt="Settings icon"
									className={styles.viewButtonIcon}
								/>
								{sidebarOpen && "Settings"}
							</div>
						</div>
					)}

					{/* Divider between Settings and Help items */}
					{!minimal && <div className={styles.navDivider} />}

					{!minimal && (
						<div className={`${styles.navItem} ${styles.mobileHideSettings}`}>
							<div
								className={`${styles.viewButton} ${styles.brandSidebarButton} ${viewMode === "support" ? styles.viewButtonSelected : ""} ${!sidebarOpen ? styles.iconOnly : ""}`}
								onClick={() => {
									onChangeView("support");
									onClearSelection();
									markSeen("support");
								}}
							>
								<User
									className={styles.viewButtonIcon}
									color="white"
									strokeWidth={1.5}
								/>
								{sidebarOpen && "Support"}
								{sidebarOpen && !seenPages.support && (
									<span className={styles.newBadge}>New</span>
								)}
							</div>
						</div>
					)}

					{!minimal && (
						<div className={`${styles.navItem} ${styles.mobileHideSettings}`}>
							<div
								className={`${styles.viewButton} ${styles.brandSidebarButton} ${viewMode === "faq" ? styles.viewButtonSelected : ""} ${!sidebarOpen ? styles.iconOnly : ""}`}
								onClick={() => {
									onChangeView("faq");
									onClearSelection();
									markSeen("faq");
								}}
							>
								<CircleHelp
									className={styles.viewButtonIcon}
									color="white"
									strokeWidth={1.5}
								/>
								{sidebarOpen && "FAQ"}
								{sidebarOpen && !seenPages.faq && (
									<span className={styles.newBadge}>New</span>
								)}
							</div>
						</div>
					)}

					{!minimal && onSwitchToAdmin && (
						<div className={`${styles.navItem} ${styles.mobileHideSettings}`}>
							<div
								className={`${styles.viewButton} ${styles.brandSidebarButton} ${!sidebarOpen ? styles.iconOnly : ""}`}
								onClick={onSwitchToAdmin}
							>
								<img
									src={useLightIcons ? analyticsWhiteIcon : analyticsWhiteIcon}
									alt="Admin icon"
									className={styles.viewButtonIcon}
								/>
								{sidebarOpen && "Admin View"}
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default Sidebar;
